import React from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import {
  INotificationMonitoringMedicinesMedicine,
  INotificationMonitoringMedicinesData,
  Notification,
} from "app/models/notification";
import { Button, Col, Row, Modal } from "react-bootstrap";
import { Card, CardContent } from "@material-ui/core";
import { convertDateTimeToTimeNoSeconds } from "_metronic/_helpers";
import { logToSentry } from "_metronic/_helpers/SentryHelper";
import { confirmMonitoringMedicinesNotification } from "api/notifications";
import { useSelector } from "react-redux";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

interface ListItemProps {
  intl: any;
  item: INotificationMonitoringMedicinesMedicine;
  medicineIndex: number;
  notificationTs: number;
}

const ListItem = (props: ListItemProps) => {
  const { intl, item, notificationTs, medicineIndex } = props;
  const { user } = useSelector((state: any) => {
    return state.auth;
  });

  const [isLoadingRequest, setIsLoadingRequest] = React.useState<boolean>(
    false
  );
  const [wasMedicineConfirmed, setWasMedicineConfirmed] = React.useState<
    boolean
  >(item.confirmation?.by ? true : false);

  function onConfirmMedicine(setIsLoading: (loading: boolean) => void) {
    setIsLoading(true);
    confirmMonitoringMedicinesNotification(notificationTs, medicineIndex)
      .then(() => {
        // Update the item state manually to reflect the confirmation because it is not
        // fetched again from API after the confirmation
        if (!item.confirmation) {
          item.confirmation = {
            by: user.email,
            timestamp: null,
          };
        }

        setWasMedicineConfirmed(true);
      })
      .catch((err) => {
        logToSentry(
          err,
          "failed to onConfirmMedicine -> MonitoringMedicinesDialogList"
        );
        console.error(
          "failed to onConfirmMedicine -> MonitoringMedicinesDialogList",
          err
        );
      })
      .finally(() => setIsLoading(false));
  }

  const PacientBlock = () => (
    <>
      <span className="text-success font-weight-bolder font-size-h6">
        <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.PACIENT" />
      </span>
      <span className="d-block text-muted font-weight-normal">
        <span className="font-size-lg">
          Nome:{" "}
          {item.patient.name ?? (
            <FormattedMessage id="MONITORING.DETAIL_DIALOG.PACIENT_VIEW.NO_NAME" />
          )}
        </span>
      </span>
      <span className="d-block text-muted font-weight-normal">
        <span className="font-size-lg">
          Setor:{" "}
          {item.sector.name ?? (
            <FormattedMessage id="MONITORING.DETAIL_DIALOG.PACIENT_VIEW.NO_NAME" />
          )}
        </span>
      </span>
    </>
  );

  const MedicineBlock = () => (
    <>
      <span className="text-primary font-weight-bolder font-size-h6">
        <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.MEDICINE" />
      </span>
      <span className="d-block text-muted font-weight-normal">
        <span className="font-size-lg">
          Horário Agendado:{" "}
          {item.medicine.time
            ? new Date(item.medicine.time).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
            : "Não informado"}
        </span>
        <span className="d-block text-muted font-weight-normal">
          <span className="font-size-lg">
            Nome:{" "}
            {item.medicine.name ?? (
              <FormattedMessage id="MONITORING.DETAIL_DIALOG.PACIENT_VIEW.NO_NAME" />
            )}
          </span>
        </span>
        <span className="d-block text-muted font-weight-normal">
          <span className="font-size-lg">
            Dose:{" "}
            {item.medicine.dosage ?? (
              <FormattedMessage id="MONITORING.DETAIL_DIALOG.PACIENT_VIEW.NO_NAME" />
            )}
          </span>
        </span>
      </span>
    </>
  );

  const ConfirmationBlock = () => {
    return (
      <div>
        {!wasMedicineConfirmed ? (
          <Button
            variant="success"
            disabled={isLoadingRequest}
            onClick={() => onConfirmMedicine(setIsLoadingRequest)}
          >
            {isLoadingRequest ? (
              <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.CONFIRMING" />
            ) : (
              <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.CONFIRM" />
            )}
          </Button>
        ) : (
          <div className="text-info font-weight-bolder font-size-h6">
            <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.CONFIRMED" />
            <CheckCircleIcon
              style={{
                color: "#5dc25d",
                fontSize: 20,
                marginLeft: 5,
                marginBottom: 5,
              }}
            />
            {item.confirmation?.by && (
              <span className="d-block text-muted font-weight-normal font-size-lg">
                <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.CONFIRMED_BY" />
                {": "}
                {item.confirmation.by}
              </span>
            )}
            {item.confirmation?.timestamp && (
              <span className="d-block text-muted font-weight-normal font-size-lg">
                <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.TIMESTAMP" />
                {": "}
                {new Date(item.confirmation.timestamp).toLocaleDateString(
                  "pt-BR",
                  {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }
                )}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Col xs={12} md={6} className="p-2">
      <Card className="card card-custom card-stretch rounded-lg h-100">
        <CardContent>
          <Row>
            <Col xs={12}>
              <PacientBlock />
              <div className="mt-4"></div>
            </Col>
            <Col xs={12}>
              <MedicineBlock />
              <div className="mt-4"></div>
            </Col>
            <Col xs={12}>
              <ConfirmationBlock />
              <div className="mt-4"></div>
            </Col>
          </Row>
        </CardContent>
      </Card>
    </Col>
  );
};

export interface PressureInjuryDialogListProps {
  intl: any;
  notification: Notification;
  onClose: () => {};
}

const MonitoringMedicinesDialogList = (
  props: PressureInjuryDialogListProps
) => {
  const { intl, notification, onClose } = props;
  const data = notification.data as INotificationMonitoringMedicinesData;

  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      centered
      show={true}
      onHide={handleClose}
      size="lg"
      backdrop="static"
    >
      <div ref={modalRef}>
        <Modal.Header closeButton>
          <Modal.Title className="flex-grow-1">
            <Row>
              <Col xs={8} lg={10}>
                <span className={`text-${notification.getSeverityStyle()}`}>
                  <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.TITLE" />
                </span>
                <span className="d-block text-primary font-size-sm font-weight-normal">
                  <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.DESCRIPTION" />
                </span>
              </Col>
              <Col xs={4} lg={2}>
                <span className={`text-info`}>
                  {convertDateTimeToTimeNoSeconds(notification.timestamp)}
                </span>
                <span className="d-block text-muted font-size-sm font-weight-normal">
                  <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.TIME" />
                </span>
              </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {data.medicines.map((el, index) => (
              <ListItem
                key={`${el.patient.medicalRecord}-${el.medicine.name}`}
                intl={intl}
                item={el}
                notificationTs={notification.timestamp}
                medicineIndex={index}
              />
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "center" }}>
          <Row style={{ flex: 1 }}>
            <Col xs={6} lg={6} className={"text-left"} />
            <Col xs={6} lg={6} className={"text-right"}>
              <Button variant="danger" onClick={onClose}>
                <FormattedMessage id="NOTIFICATIONS.ALERTS.MONITORING_MEDICINES.DIALOG.CLOSE" />
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default injectIntl(MonitoringMedicinesDialogList);
